<template>
  <div class="notices">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">お知らせ</h1>
      </div>
    </div>
    
    <section class="section">
      <div class="container">
        <div class="notices-container">
          <div class="notices-list">
            <div v-if="loading" class="loading-state">
              <p>読み込み中...</p>
            </div>
            <div v-else-if="error" class="error-state">
              <p>{{ error }}</p>
              <button @click="fetchNotices" class="btn">再読み込み</button>
            </div>
            <div v-else-if="notices.length === 0" class="empty-state">
              <p>お知らせはありません。</p>
            </div>
            <template v-else>
              <div
                class="notice-card"
                v-for="(notice, index) in notices"
                :key="notice.id"
                :class="['layout-' + (index % 2 === 0 ? 'image-left' : 'image-right')]"
              >
                <div class="notice-header">
                  <span class="notice-date">{{ formatDate(notice.date) }}</span>
                  <span v-if="notice.isImportant" class="notice-badge important">重要</span>
                </div>
                <div class="notice-body-layout">
                  <div v-if="notice.imageUrl" class="notice-image-container">
                    <img :src="notice.imageUrl" alt="お知らせ画像" class="notice-image-element">
                  </div>
                  <div class="notice-text-content">
                    <h3 class="notice-title">{{ notice.title }}</h3>
                    <div class="notice-content-wrapper">
                      <div class="notice-content" v-html="notice.content"></div>
                    </div>
                    <button @click="openModal(notice)" class="read-more-btn">続きを読む</button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal"> 
      <div class="modal-content">
        <button class="modal-close-btn" @click="closeModal">&times;</button>
        <div v-if="selectedNotice">
          <h2 class="modal-title">{{ selectedNotice.title }}</h2>
          <div class="modal-meta">
            <span class="modal-date">{{ formatDate(selectedNotice.date) }}</span>
            <span v-if="selectedNotice.isImportant" class="notice-badge important modal-badge">重要</span>
          </div>
          <div v-if="selectedNotice.imageUrl" class="modal-image">
            <img :src="selectedNotice.imageUrl" alt="お知らせ画像">
          </div>
          <div class="modal-body" v-html="selectedNotice.content"></div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted } from 'vue';

export default {
  name: 'NoticesPage',
  setup() {
    const notices = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const isModalOpen = ref(false);
    const selectedNotice = ref(null);

    const fetchNotices = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/notices');
        
        if (!response.ok) {
          throw new Error('Failed to fetch notices');
        }
        
        const data = await response.json();
        const noticesArray = data.notices || [];
        
        notices.value = noticesArray.map(notice => ({
          id: notice.id,
          title: notice.title,
          content: notice.content,
          date: notice.date,
          isImportant: notice.is_important,
          imageUrl: notice.image_url || ''
        }));
      } catch (err) {
        console.error('お知らせデータの取得に失敗しました:', err);
        error.value = 'データの取得に失敗しました。あとでもう一度お試しください。';
      } finally {
        loading.value = false;
      }
    };
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };
    
    const openModal = (notice) => {
      selectedNotice.value = notice;
      isModalOpen.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
      isModalOpen.value = false;
      selectedNotice.value = null;
      document.body.style.overflow = '';
    };
    
    onMounted(() => {
      fetchNotices();
    });
    
    return {
      notices,
      loading,
      error,
      fetchNotices,
      formatDate,
      isModalOpen,
      selectedNotice,
      openModal,
      closeModal
    };
  }
}
</script>

<style scoped>
.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 60px 0;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
}

.notices-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 0;
}

.notice-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
  margin-bottom: 30px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.notice-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.notice-header {
  padding: 12px 20px;
  background-color: #fdfdfd;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notice-date {
  color: #777;
  font-size: 13px;
}

.notice-badge {
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
}

.notice-badge.important {
  background-color: #ffebee;
  color: #c62828;
}

.notice-body-layout {
  display: flex;
  align-items: flex-start;
  padding: 25px;
  gap: 25px;
}

.notice-image-container {
  flex: 0 0 300px;
  border-radius: 8px;
  overflow: hidden;
}

.notice-image-element {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  aspect-ratio: 16 / 10;
}

.notice-text-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.layout-image-right .notice-body-layout {
  flex-direction: row-reverse;
}

.notice-title {
  font-size: 20px;
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  font-weight: 600;
}

.notice-content p {
  margin-bottom: 1em;
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 60px 0;
  color: #777;
}

.error-state button {
  margin-top: 20px;
}

.notice-content-wrapper {
  flex-grow: 1;
  overflow: hidden;
  margin-bottom: 10px;
}

.notice-content {
  color: #555;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
}

.read-more-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 500;
  cursor: pointer;
  padding: 5px 0;
  margin-top: auto;
  align-self: flex-start;
  font-size: 14px;
}

.read-more-btn:hover {
  text-decoration: underline;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.modal-content {
  background-color: white;
  padding: 30px 35px;
  border-radius: 10px;
  max-width: 800px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
}

.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 30px;
  color: #aaa;
  cursor: pointer;
  line-height: 1;
}

.modal-close-btn:hover {
  color: #333;
}

.modal-title {
  font-size: 24px;
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
}

.modal-meta {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.modal-date {
  color: #777;
  font-size: 14px;
}

.modal-badge {
  font-size: 12px;
}

.modal-image {
  margin: 20px 0;
  text-align: center;
}

.modal-image img {
  max-width: 100%;
  max-height: 50vh;
  border-radius: 8px;
}

.modal-body {
  color: #555;
  line-height: 1.8;
}

.modal-body p {
  margin-bottom: 1.2em;
}

@media (max-width: 768px) {
  .notices-container {
    max-width: 100%;
    padding: 20px 15px;
  }

  .notice-card {
    margin-bottom: 20px;
  }

  .notice-body-layout {
    flex-direction: column !important;
    padding: 20px;
    gap: 15px;
  }

  .notice-image-container {
    flex-basis: auto;
    width: 100%;
    margin-bottom: 15px;
    max-width: none;
    flex-shrink: 1;
  }

  .notice-image-element {
    aspect-ratio: 16 / 9;
  }

  .notice-title {
    font-size: 18px;
  }

  .modal-content {
    padding: 20px 25px;
    max-height: 85vh;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-close-btn {
    font-size: 26px;
    top: 8px;
    right: 10px;
  }

  .page-title {
    font-size: 26px;
  }

  .page-header {
    padding: 40px 0;
  }
}
</style> 